import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { Link } from "react-router-dom";
import "../styles/site.css";

const formatPrice = (value) => {
  const amount = Number(value);
  if (Number.isNaN(amount)) return value || "-";
  return amount.toLocaleString("en-IN");
};

const searchableText = (...values) =>
  values
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .replace(/(\d+)\s*n(ights?)?\b/g, "$1 night")
    .replace(/(\d+)\s*d(ays?)?\b/g, "$1 day")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const searchTokens = (value) => {
  const normalized = searchableText(value);
  const compact = normalized.replace(/\s+/g, "");
  return compact === normalized ? [normalized] : [normalized, compact];
};

// Durations are typed by hand ("2 Nights / 3 Days", "3Days & 2Nights"), so the card reads the
// numbers out of them and shows every trip the same way.
const durationParts = (text) => {
  const value = String(text || "");
  const nights = value.match(/(\d+)\s*n(?:ights?)?\b/i)?.[1];
  const days = value.match(/(\d+)\s*d(?:ays?)?\b/i)?.[1];
  if (!nights && !days) return null;
  return { nights: nights ? Number(nights) : null, days: days ? Number(days) : null };
};

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

const PERKS = [
  { icon: "bi-car-front", text: "Private vehicle & driver" },
  { icon: "bi-house-heart", text: "Handpicked stays" },
  { icon: "bi-geo-alt", text: "Live trip tracking" },
  { icon: "bi-wallet2", text: "Pay in stages" },
];

const TripsSection = ({ categories = [] }) => {
  const [searchQuery, setSearchQuery] = useState("");

  const orderedCategories = useMemo(
    () =>
      categories.map((category) => ({
        ...category,
        trips: [...(category.trips || [])].sort(
          (a, b) =>
            (a.displayOrder ?? Number.MAX_SAFE_INTEGER) -
              (b.displayOrder ?? Number.MAX_SAFE_INTEGER) ||
            (a.id ?? 0) - (b.id ?? 0)
        ),
      })),
    [categories]
  );

  const visibleCategories = useMemo(() => {
    const queries = searchTokens(searchQuery).filter(Boolean);
    if (!queries.length) return orderedCategories;

    return orderedCategories
      .map((category) => {
        const categoryText = category.title || "";
        const trips = category.trips.filter((trip) => {
          const haystack = searchableText(
            trip.title,
            trip.duration,
            trip.state,
            trip.path,
            categoryText
          );
          const compactHaystack = haystack.replace(/\s+/g, "");
          return queries.some(
            (query) => haystack.includes(query) || compactHaystack.includes(query)
          );
        });
        return { ...category, trips };
      })
      .filter((category) => category.trips.length > 0);
  }, [orderedCategories, searchQuery]);

  const searching = searchQuery.trim().length > 0;
  const matchCount = visibleCategories.reduce((sum, category) => sum + category.trips.length, 0);

  return (
    <div className="io-home">
      <Container className="home-search-wrap">
        <div className="home-search-panel" id="trips">
          <div className="home-search-field">
            <i className="bi bi-search" aria-hidden="true" />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Where do you want to go? Try Coorg, Kerala or 2 nights"
              aria-label="Search trips"
            />
            {searching && (
              <button type="button" className="home-search-clear" onClick={() => setSearchQuery("")} aria-label="Clear search">
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            )}
          </div>
          {searching ? (
            <div className="home-search-result" role="status">
              {matchCount ? `${plural(matchCount, "trip")} found` : `No trips found for "${searchQuery.trim()}"`}
            </div>
          ) : (
            <nav className="home-chips" aria-label="Trip lengths">
              {orderedCategories.filter((category) => category.trips.length).map((category) => (
                <a key={category.id} href={`#trips-${category.id}`} className="home-chip">{category.title}</a>
              ))}
            </nav>
          )}
        </div>
        <ul className="home-perks">
          {PERKS.map((perk) => (
            <li key={perk.text}><i className={`bi ${perk.icon}`} aria-hidden="true" />{perk.text}</li>
          ))}
        </ul>
      </Container>

      {visibleCategories.map((category) => (
        <TripRow key={category.id} category={category} />
      ))}
    </div>
  );
};

// One length of trip: a row the visitor swipes on a phone and pages with the arrows on a desktop.
function TripRow({ category }) {
  const trackRef = useRef(null);
  const [edges, setEdges] = useState({ atStart: true, atEnd: true });

  const updateEdges = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    setEdges({ atStart: track.scrollLeft <= 4, atEnd: track.scrollLeft >= max - 4 });
  }, []);

  useEffect(() => {
    updateEdges();
    window.addEventListener("resize", updateEdges);
    return () => window.removeEventListener("resize", updateEdges);
  }, [updateEdges, category.trips.length]);

  const page = (direction) => {
    const track = trackRef.current;
    if (track) track.scrollBy({ left: direction * track.clientWidth, behavior: "smooth" });
  };

  const titleId = `trips-${category.id}-title`;
  return (
    <section className="home-row" id={`trips-${category.id}`} aria-labelledby={titleId}>
      <Container>
        <div className="home-row-head">
          <div>
            <h2 className="home-row-title" id={titleId}>{category.title}</h2>
            <div className="home-row-count">{plural(category.trips.length, "trip")}</div>
          </div>
          {!(edges.atStart && edges.atEnd) && (
            <div className="home-row-arrows">
              <button type="button" onClick={() => page(-1)} disabled={edges.atStart} aria-label={`Previous ${category.title} trips`}>
                <i className="bi bi-chevron-left" aria-hidden="true" />
              </button>
              <button type="button" onClick={() => page(1)} disabled={edges.atEnd} aria-label={`More ${category.title} trips`}>
                <i className="bi bi-chevron-right" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
        <div className="home-row-track" ref={trackRef} onScroll={updateEdges}>
          {category.trips.map((trip, index) => (
            <TripCard key={trip.id ?? trip.path ?? index} trip={trip} />
          ))}
        </div>
      </Container>
    </section>
  );
}

function TripCard({ trip }) {
  const parts = durationParts(trip.duration);
  const hasPrice = Number(trip.price) > 0;
  const place = trip.state?.trim();
  return (
    <Link to={`/trips/${trip.path}`} className="home-card">
      <div className="home-card-media">
        {trip.imageUrl ? (
          <img src={trip.imageUrl} alt="" loading="lazy" />
        ) : (
          <div className="home-card-media-empty"><i className="bi bi-image" aria-hidden="true" /></div>
        )}
        {place && <span className="home-card-place"><i className="bi bi-geo-alt-fill" aria-hidden="true" />{place}</span>}
      </div>
      <div className="home-card-body">
        <h3 className="home-card-title">{String(trip.title || "").trim()}</h3>
        <div className="home-card-duration">
          {parts ? (
            <>
              {parts.nights != null && <span><i className="bi bi-moon-stars" aria-hidden="true" />{plural(parts.nights, "Night")}</span>}
              {parts.days != null && <span><i className="bi bi-sun" aria-hidden="true" />{plural(parts.days, "Day")}</span>}
            </>
          ) : trip.duration ? (
            <span><i className="bi bi-calendar3" aria-hidden="true" />{trip.duration}</span>
          ) : null}
        </div>
        <div className="home-card-foot">
          {hasPrice ? (
            <div className="home-card-price">
              <span className="home-card-from">From</span>
              <span className="home-card-amount">
                {trip.originalPrice && <del>₹{formatPrice(trip.originalPrice)}</del>}
                <strong>₹{formatPrice(trip.price)}</strong>
              </span>
              <span className="home-card-per">per person</span>
            </div>
          ) : (
            <div className="home-card-price home-card-ask">Price on request</div>
          )}
          <span className="home-card-go" aria-hidden="true"><i className="bi bi-arrow-right" /></span>
        </div>
      </div>
    </Link>
  );
}

export default TripsSection;
